/**
 * Per part costs from a CSV (B2, docs/plans/2026-10-18_actionable-platform.md; lib/api/part-costs), inside the org's
 * cost card on Settings, Organisation: an org admin picks a file with a part column and a cost per rejected casting
 * (or a casting weight in kilograms, priced at the plant's cost per kilogram), checks it first (a dry run: nothing is
 * written, the unknown parts and the refused rows are listed), then imports it. A part no product holds is never
 * created: the Catalog adds parts. PartCostsReport and partCostsHeadline are pure. No dash anywhere.
 */
import { useState, type ChangeEvent } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/lib/ui/button";
import { Input } from "@/lib/ui/input";
import { Label } from "@/lib/ui/label";
import { backendErrorMessage } from "@/lib/api";
import { importPartCosts, type PartCostImport, type PartCostRow } from "@/lib/api/part-costs";
import { TWIN_PRODUCTS_KEY } from "@/lib/api/twin-products";
import { count } from "@/lib/brief/words";
import { costText, partCostsHeadline, plural } from "@/lib/settings/part-costs";

/** How many priced rows, unknown parts and refused rows the report lists before it counts the rest. */
const SHOWN = 20;

function rowWords(p: PartCostRow, currency: string): string {
  const from =
    p.basis === "weight" && p.weight_kg !== undefined ? `, from ${p.weight_kg} kg at the cost per kilogram` : "";
  if (p.unchanged) return `${costText(p.cost, currency)}${from}, as it is`;
  const was = p.was === null ? "no cost of its own" : `was ${costText(p.was, currency)}`;
  return `${costText(p.cost, currency)}${from} (${was})`;
}

/** What a check or an import said: the parts priced, the parts no product holds, the rows refused. */
export function PartCostsReport({ result: r }: { result: PartCostImport }) {
  return (
    <div data-part-costs-report="" className="flex flex-col gap-2 rounded-md border border-border px-3 py-2 text-sm">
      <p className="font-medium text-on-surface">{partCostsHeadline(r)}</p>
      {r.priced.length > 0 && (
        <ul className="flex flex-col gap-0.5 text-xs tabular-nums text-on-surface-muted">
          {r.priced.slice(0, SHOWN).map((p) => (
            <li key={p.line}>
              <span className="text-on-surface">{p.part}</span>: {rowWords(p, r.currency)}
            </li>
          ))}
          {r.priced.length > SHOWN && <li>{`and ${plural(r.priced.length - SHOWN, "more part", "more parts")}`}</li>}
        </ul>
      )}
      {r.unknown_parts.length > 0 && (
        <p className="text-xs text-warning">
          {`No product holds these parts, so they are left out: ${r.unknown_parts.slice(0, SHOWN).join(", ")}${
            r.unknown_parts.length > SHOWN ? `, and ${count(r.unknown_parts.length - SHOWN)} more` : ""
          }. Add them on the Catalog first.`}
        </p>
      )}
      {r.errors.length > 0 && (
        <ul className="flex flex-col gap-0.5 text-xs text-danger">
          {r.errors.slice(0, SHOWN).map((e) => (
            <li key={e.line}>{`Line ${e.line}${e.part ? ` (${e.part})` : ""}: ${e.error}.`}</li>
          ))}
          {r.errors.length > SHOWN && (
            <li>{`and ${plural(r.errors.length - SHOWN, "more row", "more rows")} refused.`}</li>
          )}
        </ul>
      )}
    </div>
  );
}

/** The file picker, the check and the import. For an org admin, on the live platform. */
export function PartCostsImport() {
  const qc = useQueryClient();
  const [file, setFile] = useState<{ name: string; text: string } | null>(null);
  const [checked, setChecked] = useState<PartCostImport | null>(null);
  const [result, setResult] = useState<PartCostImport | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const pick = async (e: ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    setChecked(null);
    setResult(null);
    setError(null);
    setFile(f ? { name: f.name, text: await f.text() } : null);
  };
  const run = async (dryRun: boolean) => {
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      const r = await importPartCosts(file.text, dryRun);
      if (dryRun) {
        setChecked(r);
        setResult(null);
      } else {
        setResult(r);
        setChecked(null);
        // The Catalog's products now hold the costs.
        await qc.invalidateQueries({ queryKey: TWIN_PRODUCTS_KEY });
      }
    } catch (err) {
      setError(backendErrorMessage(err, "the per part costs"));
    } finally {
      setBusy(false);
    }
  };
  const canImport = Boolean(checked && checked.priced.some((p) => !p.unchanged));
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor="part-costs-file">Per part costs from a file</Label>
      <p className="text-xs text-on-surface-muted">
        A CSV with a header row (commas, semicolons or tabs between the columns): a part column and a cost per rejected
        casting, in the currency above; or a part column and a casting weight in kilograms, priced once at the cost
        per kilogram above. Check it first: nothing is written until you import it, and a part the Catalog does not
        hold is left out.
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <Input
          id="part-costs-file"
          type="file"
          accept=".csv,.tsv,.txt,text/csv,text/tab-separated-values,text/plain"
          className="max-w-72"
          onChange={pick}
          disabled={busy}
        />
        <Button variant="secondary" disabled={!file || busy} onClick={() => void run(true)}>
          {busy && !checked ? "Checking…" : "Check the file"}
        </Button>
        <Button disabled={!canImport || busy} onClick={() => void run(false)}>
          {busy && checked ? "Importing…" : "Import"}
        </Button>
      </div>
      {error && (
        <p role="alert" className="text-xs text-danger">
          {error}
        </p>
      )}
      {checked && <PartCostsReport result={checked} />}
      {result && <PartCostsReport result={result} />}
    </div>
  );
}
